// Shared progress math for an enrollment: completed steps / total steps on the
// longest route through the flow graph (condition branches resolve to the
// branch actually taken when the condition step completed).
//
// - TRIGGER steps never count.
// - A DELAY marked COMPLETED whose successor is still WAITING is in-flight,
//   not done — it doesn't count toward the numerator.

import { isApprovalStep, stepBranches } from '@/utils/automationBuilder';

export const computePathProgress = ({ steps = [], edges = [], enrollmentSteps = [] }) => {
    const edgeMap = {};
    (edges || []).forEach(e => {
        if (!edgeMap[e.sourceStepID]) edgeMap[e.sourceStepID] = [];
        edgeMap[e.sourceStepID].push(e);
    });

    const statusByStep = {};
    (enrollmentSteps || []).forEach(es => {
        statusByStep[es.pathStepID] = es.status;
    });

    const inFlightDelayIds = new Set();
    (enrollmentSteps || []).forEach(es => {
        if (es.status !== 'WAITING') return;
        (edges || []).forEach(e => {
            if (e.targetStepID !== es.pathStepID) return;
            const sourceStep = steps.find(s => s.id === e.sourceStepID);
            if (sourceStep?.stepType === 'DELAY' && statusByStep[e.sourceStepID] === 'COMPLETED') {
                inFlightDelayIds.add(e.sourceStepID);
            }
        });
    });

    const completed = (enrollmentSteps || []).filter(s =>
        (s.status === 'COMPLETED' || s.status === 'SKIPPED')
        && s.PathStep?.stepType !== 'TRIGGER'
        && !inFlightDelayIds.has(s.pathStepID)
    ).length;

    const visited = new Set();
    const longestFrom = (stepId) => {
        if (!stepId || visited.has(stepId)) return 0;
        visited.add(stepId);
        const step = steps.find(s => s.id === stepId);
        if (!step) { visited.delete(stepId); return 0; }

        const stepEdges = edgeMap[stepId] || [];

        // Branch-source steps (CONDITION, REQUEST_APPROVAL): once completed,
        // only the actually-taken branch counts toward the longest route.
        if ((step.stepType === 'CONDITION' || isApprovalStep(step)) && statusByStep[stepId] === 'COMPLETED') {
            const branches = stepBranches(step);
            let takenTarget = null;
            for (const branch of branches) {
                const branchEdge = stepEdges.find(e => e.branchID === branch.id);
                const targetId = branchEdge?.targetStepID;
                const targetStatus = targetId ? statusByStep[targetId] : undefined;
                if (targetStatus && targetStatus !== 'PENDING') {
                    takenTarget = targetId;
                    break;
                }
            }
            if (!takenTarget && step.stepType === 'CONDITION') {
                const defaultEdge = stepEdges.find(e => e.branchID === 'default' || (!e.branchID && e.branchLabel === 'No Match'));
                const defaultTargetId = defaultEdge?.targetStepID;
                const defaultTargetStatus = defaultTargetId ? statusByStep[defaultTargetId] : undefined;
                if (defaultTargetStatus && defaultTargetStatus !== 'PENDING') {
                    takenTarget = defaultTargetId;
                }
            }
            const result = 1 + (takenTarget ? longestFrom(takenTarget) : 0);
            visited.delete(stepId);
            return result;
        }

        let maxChild = 0;
        for (const edge of stepEdges) {
            const childLen = longestFrom(edge.targetStepID);
            if (childLen > maxChild) maxChild = childLen;
        }
        visited.delete(stepId);
        return 1 + maxChild;
    };

    const triggerStep = steps.find(s => s.stepType === 'TRIGGER');
    const triggerEdges = triggerStep ? (edgeMap[triggerStep.id] || []) : [];
    let total = 0;
    if (triggerStep) {
        visited.add(triggerStep.id);
        let maxChild = 0;
        for (const edge of triggerEdges) {
            const childLen = longestFrom(edge.targetStepID);
            if (childLen > maxChild) maxChild = childLen;
        }
        total = maxChild;
    }

    return {
        completedCount: completed,
        longestPathTotal: total,
        progressPercent: total > 0 ? (completed / total) * 100 : 0,
    };
};
